import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ModuleState } from '@/api/modules';
import { useAuthStore } from '@/auth/authStore';
import { queryClient } from '@/app/queryClient';

interface ModuleStoreState {
  /** Module key → whether the organisation has it on. Core modules are always present and true. */
  enabled: Record<string, boolean>;
  /** Names by key, for the "module not enabled" card. */
  names: Record<string, string>;
  /** Whether the server has answered at least once (persisted, so a reload renders from the last answer). */
  loaded: boolean;
  /** Facts a module reports about the organisation's set-up that the menu depends on (e.g. `property.airport`: a portfolio the person sees has the airport pack on). */
  features: Record<string, boolean>;
  setModules: (modules: ModuleState[]) => void;
  setFeature: (key: string, on: boolean) => void;
  isEnabled: (key: string) => boolean;
  clear: () => void;
}

/**
 * Which modules the signed-in user's organisation has switched on. Persisted so the sidebar and
 * routes render from the last known answer instead of flickering on reload; `useModuleSync`
 * refreshes it from the server whenever the layout mounts, the tab regains focus, a module is
 * switched, or the API answers 403 `ModuleDisabled`.
 */
export const useModuleStore = create<ModuleStoreState>()(
  persist(
    (set, get) => ({
      enabled: {},
      names: {},
      loaded: false,
      features: {},
      setFeature: (key, on) => { if (get().features[key] !== on) set((s) => ({ features: { ...s.features, [key]: on } })); },
      setModules: (modules) =>
        set({
          enabled: Object.fromEntries(modules.map((m) => [m.key, m.enabled])),
          names: Object.fromEntries(modules.map((m) => [m.key, m.name])),
          loaded: true,
        }),
      isEnabled: (key) => get().enabled[key] === true,
      clear: () => set({ enabled: {}, names: {}, loaded: false, features: {} }),
    }),
    { name: 'zsic-sbp-modules' },
  ),
);

/** Non-hook accessors for use outside React. */
export const moduleStore = {
  getState: useModuleStore.getState,
  setState: useModuleStore.setState,
};

// A different person signing in, or the session ending, must not inherit the previous
// organisation's modules: forget the store and the cached answers.
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    useModuleStore.getState().clear();
    queryClient.removeQueries({ queryKey: ['modules'] });
  }
});
